import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { VisitorFeedbackAnalyticsDto } from '@/Domain/api/Analytics/dto/VisitorFeedbackAnalyticsDto';
import { QuestionCodeEnum } from '@/Domain/api/Analytics/enums/QuestionCode.enum';
import { ResponseCodeEnum } from '@/Domain/api/Analytics/enums/ResponseCode.enum';
import { ReasonCodeEnum } from '@/Domain/enum/ReasonCode.enum';
import { DateRangeState } from '@/Domain/types/DateRange.types';

export const useConversationRatingData = (range: DateRangeState) => {
  const { t } = useTranslation(['insights_page', 'dashboard', 'website']);

  const { data: feedbackData, isLoading: isFeedbackLoading } =
    useQuery<VisitorFeedbackAnalyticsDto>({
      queryKey: [
        QueryKey.analytics.conversationRating,
        {
          start_date: format(range.startDate, 'yyyy-MM-dd'),
          end_date: format(range.endDate, 'yyyy-MM-dd'),
        },
      ],
    });

  // Chart configurations
  const chatExperienceConfig = useMemo(
    () =>
      ({
        positive: {
          label: t('dashboard:conversations.positive', 'Positive'),
          color: 'var(--chart-positive)',
        },
        negative: {
          label: t('dashboard:conversations.negative', 'Negative'),
          color: 'var(--chart-negative)',
        },
      }) as const,
    [t],
  );

  const aiHelpfulConfig = useMemo(
    () =>
      ({
        yes: { label: t('dashboard:conversations.yes', 'Yes'), color: 'var(--chart-positive)' },
        no: { label: t('dashboard:conversations.no', 'No'), color: 'var(--chart-negative)' },
      }) as const,
    [t],
  );

  const whyNotHelpfulConfig = useMemo(
    () =>
      ({
        [ReasonCodeEnum.TOO_TECHNICAL]: {
          label: t('dashboard:conversations.too_technical', 'Too technical'),
          color: 'var(--chart-primary-1)',
        },
        [ReasonCodeEnum.INCOMPLETE]: {
          label: t('dashboard:conversations.incomplete', 'Incomplete'),
          color: 'var(--chart-primary-2)',
        },
        [ReasonCodeEnum.NOT_RELATED]: {
          label: t('dashboard:conversations.not_related', 'Not related'),
          color: 'var(--chart-primary-3)',
        },
        [ReasonCodeEnum.INCORRECT_LINKS]: {
          label: t('dashboard:conversations.incorrect_links', 'Incorrect links'),
          color: 'var(--chart-primary-4)',
        },
        [ReasonCodeEnum.OTHER]: {
          label: t('dashboard:conversations.other', 'Other'),
          color: 'var(--chart-primary-5)',
        },
      }) as const,
    [t],
  );

  // Chat experience chart data
  const chatExperienceData = useMemo(() => {
    const stats =
      feedbackData?.visitor_feedback_analytics?.find(
        (f) => f.question_code === QuestionCodeEnum.EXPERIENCE_THUMBS,
      )?.stats || [];
    const positive = stats.find((s) => s.response_code === ResponseCodeEnum.POSITIVE)?.count || 0;
    const negative = stats.find((s) => s.response_code === ResponseCodeEnum.NEGATIVE)?.count || 0;
    return [{ positive, negative }];
  }, [feedbackData]);

  const chatExperienceTotal = useMemo(
    () => chatExperienceData[0]?.positive + chatExperienceData[0]?.negative || 0,
    [chatExperienceData],
  );

  // AI helpful chart data
  const aiHelpfulData = useMemo(() => {
    const stats =
      feedbackData?.visitor_feedback_analytics?.find(
        (f) => f.question_code === QuestionCodeEnum.HELPFUL_THUMBS,
      )?.stats || [];
    const yes = stats.find((s) => s.response_code === ResponseCodeEnum.POSITIVE)?.count || 0;
    const no = stats.find((s) => s.response_code === ResponseCodeEnum.NEGATIVE)?.count || 0;
    return [{ yes, no }];
  }, [feedbackData]);

  const aiHelpfulTotal = useMemo(
    () => aiHelpfulData[0]?.yes + aiHelpfulData[0]?.no || 0,
    [aiHelpfulData],
  );

  // Why not helpful chart data
  const whyNotHelpfulData = useMemo(() => {
    const stats =
      feedbackData?.visitor_feedback_analytics?.find(
        (f) => f.question_code === QuestionCodeEnum.HELPFUL_WHY,
      )?.stats || [];
    const technical =
      stats.find((s) => s.response_code === ResponseCodeEnum.TOO_TECHNICAL_REPLIES)?.count || 0;
    const incomplete =
      stats.find((s) => s.response_code === ResponseCodeEnum.INCOMPLETE_ANSWER)?.count || 0;
    const notRelated =
      stats.find((s) => s.response_code === ResponseCodeEnum.NOT_RELATED_TO_MY_PROBLEM)?.count || 0;
    const incorrectLinks =
      stats.find((s) => s.response_code === ResponseCodeEnum.INCORRECT_LINKS)?.count || 0;
    const other = stats.find((s) => s.response_code === ResponseCodeEnum.OTHER)?.count || 0;

    const data = [
      { reason: ReasonCodeEnum.TOO_TECHNICAL, count: technical },
      { reason: ReasonCodeEnum.INCOMPLETE, count: incomplete },
      { reason: ReasonCodeEnum.NOT_RELATED, count: notRelated },
      { reason: ReasonCodeEnum.INCORRECT_LINKS, count: incorrectLinks },
      { reason: ReasonCodeEnum.OTHER, count: other },
    ];

    return data.map((item, idx) => ({ ...item, fill: `var(--chart-primary-${(idx % 10) + 1})` }));
  }, [feedbackData]);

  return {
    feedbackData,
    isLoading: isFeedbackLoading,
    chatExperienceData,
    chatExperienceTotal,
    chatExperienceConfig,
    aiHelpfulData,
    aiHelpfulTotal,
    aiHelpfulConfig,
    whyNotHelpfulData,
    whyNotHelpfulConfig,
  };
};
